/**
 * @file Client for `GET /api/search-airport/v3` (airport & city search).
 *
 * v3 replaces the old `POST search-airport/v2`. Two things changed that matter
 * here: results come back **ranked** (do not re-sort or re-group them), and a
 * result can be a *city* — a metro area like NYC that contains JFK/LGA/EWR.
 *
 * `normalizeResult` deliberately returns a **superset** of the old v2 row so
 * every existing consumer keeps working untouched. Consumers across the app
 * read `.id` (persisted as `home_airport_id` / `destination_airport_id` /
 * `subject_airport_id`), `.iata`, `.name`, `.city` and `.country_name`; those
 * are kept as aliases over the v3 fields. New code should prefer the canonical
 * v3 names (`resolved_airport_id`, `code`, `label`, `sub_label`).
 *
 * There is no endpoint that converts an IATA code back into a database id, and
 * `airports.iata` is not unique — so `resolved_airport_id` is the only durable
 * handle. Always persist it.
 */
import callAzureFunction from 'utils/callAzureFunctions';

/** Below this many characters we skip the request — single letters rank poorly. */
export const MIN_QUERY_LENGTH = 2;

/** The API clamps `limit` to 1–25 and defaults to 8. */
export const DEFAULT_LIMIT = 8;

/** API p50 is ~35ms (~75ms when it falls through to fuzzy matching). */
export const SEARCH_DEBOUNCE_MS = 200;

/** Sentinel returned instead of throwing when a request was superseded. */
export const ABORTED = Symbol('airport-search/aborted');

const CACHE_LIMIT = 60;

/**
 * Insertion-ordered LRU. The endpoint sends `Cache-Control: private,
 * max-age=300` so the browser already caches repeats, but holding results in
 * memory makes backspacing through a query instant with no network at all.
 */
const cache = new Map();

const cacheKey = ({ query, limit, mode }) => `${mode}|${limit}|${query.toLowerCase()}`;

const readCache = (key) => {
  if (!cache.has(key)) return undefined;
  // Re-insert to mark as most-recently used.
  const hit = cache.get(key);
  cache.delete(key);
  cache.set(key, hit);
  return hit;
};

const writeCache = (key, value) => {
  cache.set(key, value);
  if (cache.size > CACHE_LIMIT) {
    cache.delete(cache.keys().next().value);
  }
};

/** Exposed for tests and for the rare case a caller needs a cold read. */
export const clearAirportSearchCache = () => cache.clear();

/**
 * `mode` is an enum of `all | us_only`. v2 call sites pass v2's `US_only`
 * casing (see PointsPortal), and anything that isn't `us_only` behaves as
 * `all`, so fold the legacy spelling rather than passing it through.
 */
export const normalizeSearchMode = (mode) => (String(mode || '').toLowerCase() === 'us_only' ? 'us_only' : 'all');

/**
 * Map one raw v3 result into the shape the app uses.
 *
 * @param {object} raw     A `results[]` entry, or an entry of a city's `airports[]`.
 * @param {object|null} parent  The city result when `raw` is one of its member airports.
 */
export const normalizeResult = (raw, parent = null) => {
  const isCity = raw.type === 'city';
  const airports = isCity ? (raw.airports || []).map((airport) => normalizeResult(airport, raw)) : undefined;
  const parentCode = parent?.code ?? null;

  return {
    // ---- canonical v3 ----
    type: raw.type,
    code: raw.code,
    resolved_airport_id: raw.resolved_airport_id,
    label: raw.label,
    // Pre-formatted secondary line: already drops missing parts and shortens
    // formal country names. Never rebuild it from city/state/country.
    sub_label: raw.sub_label,
    icao: raw.icao || null,
    latitude: raw.latitude ?? null,
    longitude: raw.longitude ?? null,
    popularity_score: raw.popularity_score ?? null,
    airport_count: isCity ? (raw.airport_count ?? airports.length) : undefined,
    airports,

    // ---- v2-compatible aliases (existing consumers read these) ----
    id: raw.resolved_airport_id,
    iata: raw.code,
    name: raw.label,
    city: raw.city || '',
    // The API returns '' rather than null for a missing state (e.g. Manila),
    // and some US states arrive hyphenated ('New-York'). Treat falsy as absent.
    state: raw.state || '',
    country: raw.country || '',
    country_name: raw.country_name || '',

    // ---- local provenance ----
    /** Metro code this row was nested under, or null for a top-level result. */
    parent_city_code: parentCode,
    /** Metro display name, for a nested row's accessible name ("in the New York metro area"). */
    parent_city_label: parent?.label ?? null,
    /** The parent's `city`, so a child can tell whether its own differs (EWR → Newark). */
    parent_city_name: parent?.city || null,
    /** Stable, collision-free identity for React keys and Combobox values. */
    uid: parentCode
      ? `${parentCode}>${raw.type}:${raw.code}:${raw.resolved_airport_id}`
      : `${raw.type}:${raw.code}:${raw.resolved_airport_id}`
  };
};

/**
 * Query the airport/city search.
 *
 * Resolves to `{ query, count, results }` with normalized results, or the
 * `ABORTED` sentinel when `signal` fired. Throws only on a real failure —
 * "nothing matched" is a normal `200` with an empty `results` array.
 *
 * @param {object}          options
 * @param {string}          options.q       Search text. 1–64 chars after trimming.
 * @param {number}         [options.limit]  1–25, defaults to 8.
 * @param {string}         [options.mode]   `all` or `us_only`.
 * @param {AbortSignal}    [options.signal] Aborts an in-flight request.
 */
export const searchAirports = async ({ q, limit = DEFAULT_LIMIT, mode, signal }) => {
  // The API rejects blank `q` and anything over 64 chars with a 400.
  const query = String(q ?? '')
    .trim()
    .slice(0, 64);
  const resolvedMode = normalizeSearchMode(mode);
  const resolvedLimit = Math.min(25, Math.max(1, Math.trunc(limit) || DEFAULT_LIMIT));

  if (query.length < MIN_QUERY_LENGTH) {
    return { query, count: 0, results: [] };
  }

  const key = cacheKey({ query, limit: resolvedLimit, mode: resolvedMode });
  const cached = readCache(key);
  if (cached) return cached;

  let response;
  try {
    response = await callAzureFunction({
      url: 'search-airport/v3',
      method: 'get',
      params: { q: query, limit: resolvedLimit, mode: resolvedMode },
      signal
    });
  } catch (error) {
    if (signal?.aborted || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return ABORTED;
    }
    throw error;
  }

  const payload = response?.data || {};
  const normalized = {
    query: payload.query ?? query,
    count: payload.count ?? 0,
    results: (payload.results || []).map((result) => normalizeResult(result))
  };

  writeCache(key, normalized);
  return normalized;
};

/**
 * Compact one-line text for a filled input or a pill.
 *
 * Deliberately not `sub_label` — that is a two-line-row secondary and is often
 * redundant on its own ("New York, New York, United States"). The code carries
 * the disambiguation, so "New York (JFK)" reads better in a collapsed field
 * than either the bare city or the full airport name.
 *
 * Tolerates legacy server-shaped airports too: `FlightDealTrackEditor` and
 * `AIFlightDealsDialog` seed their value from `*_airport_info`, which carries
 * v2's `iata`/`city`/`name` and none of v3's `code`/`label`/`type`.
 */
export const formatSelection = (item) => {
  if (!item) return '';
  const code = item.code || item.iata || '';
  const primary = item.type === 'city' ? item.label : item.city || item.label || item.name || '';
  if (!primary) return code;
  return code ? `${primary} (${code})` : primary;
};

/**
 * Identity of a *selection*, independent of where the row was rendered.
 *
 * `resolved_airport_id` alone is not enough: a city points at its primary
 * airport, so New York (city) and JFK (airport) both carry 2598 — keying a
 * selection set on the id would mark one as chosen when the user picked the
 * other. Conversely `code` alone is not enough either, since 41 IATA codes are
 * duplicated across rows. The triple is unique, and it deliberately matches a
 * nested airport to the same airport listed top-level.
 *
 * Falls back to v2's `iata`/`id` so a value seeded from `*_airport_info` still
 * matches the same airport once it comes back from a v3 search.
 */
export const selectionKey = (item) => {
  if (!item) return '';
  const type = item.type || 'airport';
  const code = item.code || item.iata || '';
  const id = item.resolved_airport_id ?? item.id ?? '';
  return `${type}:${code}:${id}`;
};

/**
 * Flatten ranked results into the single list Mantine's Combobox renders.
 *
 * Order is the API's ranking, with each city's member airports inlined
 * directly beneath their parent. Every entry carries `depth` (0 top-level,
 * 1 nested) so the row renderer can indent without re-deriving structure.
 *
 * Member airports are always visible. There is deliberately no collapse: the
 * only keys available to drive a disclosure toggle inside `role="option"` are
 * already taken (←/→ move the caret, Space types a space in "new york"), and a
 * real `<button>` nested in an option is invalid ARIA. A toggle would therefore
 * be mouse-only — a WCAG 2.1.1 failure rather than a missing nicety. Metros hold
 * 2–6 airports, so the unabridged list stays short.
 *
 * @param {Array}   results          Normalized results.
 * @param {object} [options]
 * @param {boolean}[options.allowCities]  When false, a city row is emitted as a
 *   non-selectable group header. Its airports stay selectable. Use this where a
 *   bare IATA string is persisted — a metro code like `NYC` is not an airport.
 * @param {Set}    [options.selectedKeys] `selectionKey()`s already chosen
 *   (multi-select) — those rows render with a check.
 */
export const flattenResults = (results, { allowCities = true, selectedKeys } = {}) => {
  const rows = [];

  results.forEach((result) => {
    if (result.type === 'city') {
      const children = result.airports || [];

      rows.push({
        ...result,
        depth: 0,
        // With cities disallowed the parent degrades to a pure header so the
        // metro is still legible, but only real airports can be picked.
        role: allowCities ? 'city' : 'cityHeader',
        selectable: allowCities,
        selected: allowCities && (selectedKeys?.has(selectionKey(result)) ?? false),
        childCount: children.length
      });

      children.forEach((airport, index) => {
        rows.push({
          ...airport,
          depth: 1,
          role: 'airport',
          selectable: true,
          selected: selectedKeys?.has(selectionKey(airport)) ?? false,
          isLastChild: index === children.length - 1
        });
      });
      return;
    }

    rows.push({
      ...result,
      depth: 0,
      role: 'airport',
      selectable: true,
      selected: selectedKeys?.has(selectionKey(result)) ?? false
    });
  });

  return rows;
};

export default searchAirports;
